import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PillModule } from '@epicuro-next/ataraxia/pill';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { isNullish } from '@epicuro-next/utilities';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { dynamicElementsPillsNavigation } from '@epicuro-next/constants/dynamic-elements';
import { pillsNavigationDefaultConfig } from './pills-navigation-default-config';
import { DontTreeShake } from '@epicuro-next/elements/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { toggleFilter } from '@epicuro-next/state/p-basket-state';

interface NavigationPill {
  value: string;
  link?: string;
  filter?: {
    fieldId: string;
    displayName: string;
    value: string;
  };
}

@DynamicComponentRegister(dynamicElementsPillsNavigation)
@Component({
  selector: 'atx-pills-navigation',
  imports: [CommonModule, PillModule, IconsModule],
  templateUrl: './pills-navigation.component.html',
  styleUrl: './pills-navigation.component.scss',
})
export class PillsNavigationComponent {
  public router = inject(Router);
  public store = inject(Store);
  public config = input(pillsNavigationDefaultConfig);
  public wrapItems = input(false);

  public selectedPills = signal<NavigationPill[]>([]);
  public stackPills = signal<NavigationPill[][]>([]);

  private configMap = computed(() => {
    const map = new Map<string, NavigationPill[]>(
      this.config() as [string, NavigationPill[]][],
    );
    return map;
  });

  public visiblePills = computed(() => this.stackPills()?.at(-1) ?? []);

  constructor() {
    effect(() => {
      const initialPills = Array.from(this.configMap().values())[0];
      if (initialPills) {
        this.stackPills.set([initialPills]);
      }
    });
  }

  public selectPill(pill: NavigationPill) {
    if (pill.filter) {
      this.store.dispatch(
        toggleFilter({
          fieldId: pill.filter.fieldId,
          value: pill.filter.value,
          replaceUrl: false,
        }),
      );
    }
    if (isNullish(pill.link)) {
      return;
    }
    if (pill.link && pill.link.startsWith('#')) {
      const nextSetKey = pill.link.substring(1);
      const nextPills = this.getPillsFromConfig(nextSetKey);

      if (nextPills) {
        this.pushToSignalArray(this.stackPills, nextPills);
      } else {
        return;
      }
    } else {
      this.router.navigateByUrl(pill.link);
      return;
      // this.store.dispatch(
      //   recommendationUrlRedirect({
      //     panelName: 'FiltersShortcutButton',
      //     providerId: 'ProductSearch',
      //     panelStyle: 'SCB',
      //     routerLink: url,
      //   }),
      // );
    }
    this.pushToSignalArray(this.selectedPills, pill);
  }

  public removeSelected(pill: NavigationPill) {
    if (pill.filter) {
      this.store.dispatch(
        toggleFilter({
          fieldId: pill.filter.fieldId,
          value: pill.filter.value,
          replaceUrl: false,
        }),
      );
    }
    this.popFromSignalArray(this.stackPills);
    this.popFromSignalArray(this.selectedPills);
  }

  private getPillsFromConfig(key: string): NavigationPill[] | undefined {
    return this.configMap().get(key);
  }

  private pushToSignalArray<T>(signalArray: WritableSignal<T[]>, newItem: T) {
    signalArray.update((array) => [...array, newItem]);
  }

  private popFromSignalArray<T>(signalArray: WritableSignal<T[]>) {
    signalArray.update((array) => array.slice(0, -1));
  }
}

DontTreeShake(PillsNavigationComponent);
